import { useMemo } from 'react'

import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { messageHistoryService } from '@/modules/log-of-all-messages/service/message-history-service.ts'
import { useMessageHistoryFilter } from '@/modules/log-of-all-messages/use-message-history-filter.tsx'
import { handleError } from '@/utils/error'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'
import { getSortDirection } from '@/utils/pagination/pagination-utils.ts'

export const useSentMessages = () => {
  const creatorId = useUnit($creatorId)
  const { pagination, searchQuery, sorting } = usePagination()
  const { filter } = useMessageHistoryFilter()

  const [sort] = sorting
  const sortDirection = getSortDirection(sort)

  const { data, isFetching, refetch } = useQuery(
    [
      'log-of-all-messages',
      creatorId,
      pagination,
      searchQuery,
      sorting,
      filter,
    ],
    async () => {
      return await messageHistoryService.getSentMessages(
        creatorId!,
        sortDirection,
        {
          ...filter,
          message: searchQuery,
        },
        {
          size: pagination.pageSize,
          page: pagination.pageIndex,
        },
      )
    },
    {
      enabled: !!creatorId,
      onError: (error) => handleError(error, 'Failed to get sent messages'),
    },
  )

  return useMemo(
    () => ({
      data,
      isFetching,
      refetch,
    }),
    [data, isFetching, refetch],
  )
}

export const useSenders = () => {
  const creatorId = useUnit($creatorId)

  const { data, isFetching, refetch } = useQuery(
    ['log-of-all-messages-senders', creatorId],
    async () => {
      return messageHistoryService.getAllSenders(creatorId!)
    },
    {
      enabled: !!creatorId,
      onError: (error) => handleError(error, 'Failed to get all senders'),
    },
  )

  return useMemo(
    () => ({
      data,
      isFetching,
      refetch,
    }),
    [data, isFetching, refetch],
  )
}
